// Video episode discovery (§10.1). Turns a resolved video source into a numbered
// episode list. A single file is episode 1. In a directory: if every file
// carries an episode number, reuse them (and reject duplicates); if none do,
// natural-sort and assign ep 1..N; mixing numbered and unnumbered files fails
// up front rather than guessing an order.

import * as path from "node:path";

import { CliError, EXIT_INPUT } from "../../common.js";
import { VIDEO_MIME, type ResolvedSource } from "./source-resolver.js";

export interface VideoEpisode {
  episode: number;
  path: string;
  ext: string;
  mime: string;
}

function ext(p: string): string {
  return path.extname(p).toLowerCase();
}

function mimeFor(p: string): string {
  return VIDEO_MIME[ext(p)] ?? "application/octet-stream";
}

// Prefer an explicit episode marker before falling back to the first run of
// digits. Real series names commonly carry unrelated numbers (for example
// `749局-第35集.mp4`); treating the title number as the episode collapses an
// entire directory into one duplicate episode.
function episodeNumber(file: string): number | null {
  // Strip the extension first — `.mp4`/`.mpeg` carry digits that are not episode
  // numbers.
  const stem = path.basename(file, path.extname(file));
  const explicit = stem.match(/第\s*0*(\d+)\s*[集话回]/u)
    ?? stem.match(/(?:^|[._\-\s])(?:ep(?:isode)?|e)\s*[._\-\s]*0*(\d+)(?=$|[._\-\s])/iu);
  if (explicit) return Number(explicit[1]);
  const m = stem.match(/\d+/);
  return m ? Number(m[0]) : null;
}

function discoveryError(message: string, received: string[]): CliError {
  return new CliError(message, {
    exitCode: EXIT_INPUT,
    errorCode: "INGEST_VIDEO_DISCOVERY",
    hint: "Name every episode file with a number, or none of them, and rerun `scriptctl ingest`.",
    data: { actual: received },
  });
}

function episode(file: string, n: number): VideoEpisode {
  return { episode: n, path: file, ext: ext(file), mime: mimeFor(file) };
}

export function discoverEpisodes(source: ResolvedSource): VideoEpisode[] {
  if (source.kind === "text") {
    throw new CliError("discoverEpisodes received a text source.", {
            exitCode: EXIT_INPUT,
            errorCode: "INGEST_WRONG_PIPELINE",
          });
  }
  if (source.kind === "video-file") return [episode(source.path, 1)];

  const files = source.files; // already one-level, allowlisted, sorted (source-resolver)
  const numbers = files.map(episodeNumber);
  const numbered = numbers.filter((n): n is number => n !== null);

  if (numbered.length === 0) {
    // None numbered: keep the resolver's natural sort, assign 1..N.
    return files.map((file, i) => episode(file, i + 1));
  }
  if (numbered.length !== files.length) {
    const bad = files.filter((_, i) => numbers[i] === null).map((f) => path.basename(f));
    throw discoveryError("Some episode files carry a number and some do not.", [`unnumbered: ${bad.join(", ")}`]);
  }
  const seen = new Set<number>();
  for (const n of numbered) {
    if (seen.has(n)) throw discoveryError("Two episode files resolve to the same number.", [`duplicate episode: ${n}`]);
    seen.add(n);
  }
  return files.map((file, i) => episode(file, numbers[i]!)).sort((a, b) => a.episode - b.episode);
}
